import { CommonModule } from '@angular/common';
import { Component, Input, QueryList, ViewChildren, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-bookslot',
    templateUrl: './bookslot.component.html',
    styleUrls: ['./bookslot.component.css'],
    standalone: false
})
export class BookslotComponent implements OnDestroy {

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  @ViewChild('dateTabsScroll') dateTabsScrollRef!: ElementRef<HTMLElement>;

  canScrollLeft = false;
  canScrollRight = false;

  mobileMenuOpen = false;
  @Input() data: any;
  currentYear = new Date().getFullYear();
  profileImg: string | null = null;
  profileData: any;
  lawyer: any;
  slots: any[] = [];
  selectedDay: any;
  filteredSlots: any[] = [];
  selectedDate: string = 'today';
  lawyerId: string = '';

  selectedDateKey: string = '';
  dateTabs: any[] = [];
  isLoading: boolean = true;
  loadingError: string = '';

  // ── Single Lawyer Details: profile tabs (Overview / Available Slots) ──
  activeProfileTab: 'overview' | 'slots' = 'slots';
  bioExpanded: boolean = false;
  casesExpandedMap: { [index: number]: boolean } = {};
  // ── Modal state ──────────────────────────────────────────────
  showLoginModal: boolean = false;
  private _pendingDay: any = null;
  private _pendingSlot: any = null;

  // ── Login/Register Form State ────────────────────────────────
  loginForm!: FormGroup;
  registerForm!: FormGroup;
  submitted = false;
  hidePassword = false;
  useOTP: boolean = true;
  otpSent: boolean = false;
  
  // Mode toggles
  isRegisterMode: boolean = false; 
  loginType: 'email' | 'mobile' = 'email';
  registerType: 'email' | 'mobile' = 'email';

  // ── Forgot / Reset Password (in-modal) ───────────────────────
  modalView: 'login' | 'forgot' | 'reset' = 'login';
  forgotPasswordForm!: FormGroup;
  resetPasswordForm!: FormGroup;
  forgotSubmitted = false;
  resetSubmitted = false;
  hideResetPassword = false;
  hideConfirmPassword = false;
  resetMail: string = '';

  otpDigits: string[] = ['', '', '', '', '', ''];
  resendTimer: number = 60;
  canResend: boolean = false;
  currentEmailOrMobile: string = '';
  
  // Storage for registration details
  registeredFirstName: string = '';
  registeredLastName: string = '';

  // ── Register Confirmation Dialog (mirrors RegisterComponent) ──
  showConfirmDialog: boolean = false;
  confirmEmail: string = '';
  confirmPhone: string = '';

  // True once the backend has told us the email/mobile is already
  // registered and we've switched from "create account" to "log in via
  // OTP" for this attempt. Drives which API verifyRegisterOTP() calls.
  isLoginRedirectFlow: boolean = false;

  // Email entered for this registration attempt, kept so we can offer it
  // as the OTP channel if the backend responds with redirect_to === 'email'/'both'.
  private pendingEmail: string = '';

  // Message shown in the register flow's "OTP sent to your ..." banner.
  // Set dynamically from the backend response (or the redirect_to-derived
  // message) instead of being hardcoded, since the actual delivery channel
  // can be email or mobile depending on what the API decided.
  otpSentMessage: string = '';

  private timerInterval: any;
  isLoggedIn = false;
  profileImage: string = '';
  firstName: string = '';
  
  private destroy$ = new Subject<void>();

  constructor(
    private auth: AuthService,
    public router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    const token = localStorage.getItem('TOKEN');
    this.isLoggedIn = !!token;

    if (this.isLoggedIn) {
      this.profileImage = localStorage.getItem('profileImg') || '';
      this.firstName = localStorage.getItem('first_name') || '';
    }
    
    this.buildLoginForm();
    this.buildRegisterForm();
    this.buildForgotPasswordForm();
    this.buildResetPasswordForm();

    let lawyerId = history.state?.lawyerId;

    if (lawyerId) {
      localStorage.setItem('lawyerid', lawyerId);
    }
    let storedId:any = localStorage.getItem('lawyerid');

    // console.log('Received lawyerId from state:', lawyerId);
    // console.log('Received storedid from state:', storedId);

    //if (!lawyerId) return;

      // Start loading immediately
    this.isLoading = true;
    this.loadingError = '';

    // Fetch lawyer profile with proper error handling
    this.auth.getLawyerProfile(lawyerId || storedId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          try {
            if (!res?.data) {
              this.loadingError = 'Failed to load lawyer profile. Please try again.';
              this.isLoading = false;
              console.error('Invalid response structure:', res);
              return;
            }

            this.lawyer = res.data;
            this.lawyerId = lawyerId || storedId;

            this.slots = res.data?.availability?.slots || [];

            if (this.slots.length === 0) {
              this.loadingError = 'No available slots found for this lawyer.';
              this.isLoading = false;
              return;
            }

            this.slots = this.slots.map(day => this.categoriseSlots(day));

            this.buildDateTabs();
            setTimeout(() => this.updateScrollArrows(), 100);

            const todayKey = this.getDateKey(new Date());
            const defaultDate =
              this.slots.find(d => d.date === todayKey)?.date || this.slots[0]?.date;

            if (defaultDate) {
              this.selectedDateKey = defaultDate;
              this.filterByExactDate(defaultDate);
            }

            this.profileImg = this.lawyer?.profile?.profile_pic_url || null;
            
            // Mark loading as complete on success
            this.isLoading = false;
          } catch (error) {
            this.loadingError = 'An error occurred while processing the data.';
            this.isLoading = false;
            console.error('Error processing lawyer data:', error);
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.loadingError = error?.error?.msg || 'Failed to load lawyer details. Please try again.';
          console.error('API Error:', error);
          
          // Show error toast
          this.toastr.error(this.loadingError, 'Error');
        }
      });
  }
  ngOnDestroy() {
    this.stopResendTimer();
    this.destroy$.next();
    this.destroy$.complete();
    // Restore body scroll in case the login modal was open (openLoginModal
    // sets overflow: hidden) when the user navigated away — otherwise the
    // next page (e.g. checkout) is left unscrollable.
    document.body.style.overflow = '';
  }

  // ── Form Setup ────────────────────────────────────────────────

  buildLoginForm() {
    this.loginForm = this.fb.group({
      emailOrMobile: ['', [Validators.required, this.validateEmailOrMobile.bind(this)]],
      password:      [''],
      otp:           ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      rememberMe:    [false]
    });
    this.loginForm.get('password')?.clearValidators();
    this.loginForm.get('password')?.updateValueAndValidity();
  }

  buildRegisterForm() {
    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName:  ['', [Validators.required]],
      email:     ['', [Validators.required, this.validateEmail.bind(this)]],
      mobile:    ['', [Validators.required, this.validateMobile.bind(this)]],
      otp:       ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  buildForgotPasswordForm() {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  buildResetPasswordForm() {
    this.resetPasswordForm = this.fb.group({
      token:           ['', Validators.required],
      password:        ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.checkPasswordsMatch.bind(this) });
  }

  private checkPasswordsMatch(group: FormGroup) {
    const pass = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return pass === confirm ? null : { notSame: true };
  }

  get rf() { return this.resetPasswordForm.controls; }

  get f() { return this.loginForm.controls; }

  validateEmailOrMobile(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const mobilePattern = /^[0-9]{10,15}$/;
    return emailPattern.test(value) || mobilePattern.test(value) ? null : { invalidEmailOrMobile: true };
  }

  detectLoginType(value: string): 'email' | 'mobile' {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ? 'email' : 'mobile';
  }

  /**
   * Validate email format (register form)
   */
  validateEmail(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (emailPattern.test(value.trim())) {
      return null;
    }
    return { invalidEmail: true };
  }

  /**
   * Validate mobile number (register form):
   * - exactly 10 digits
   * - must start with 6, 7, 8, or 9
   * Leading zeros are stripped before validation
   */
  validateMobile(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;

    const cleaned = String(value).replace(/^0+/, '');
    const mobilePattern = /^[6-9]\d{9}$/;

    if (mobilePattern.test(cleaned)) {
      return null;
    }
    return { invalidMobile: true };
  }

  /**
   * Strip leading zeros/non-digits from the register form's mobile input in real time
   */
  onMobileInput(event: any): void {
    let inputValue: string = event.target.value;
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.replace(/^0+/, '');
    event.target.value = inputValue;
    this.registerForm.get('mobile')?.setValue(inputValue, { emitEvent: true });
  }

  // ── Modal Lifecycle ───────────────────────────────────────────

  get pendingDay() { return this._pendingDay; }
  get pendingSlot() { return this._pendingSlot; }

  openLoginModal(day: any, slot: any) {
    this._pendingDay = day;
    this._pendingSlot = slot;
    this.showLoginModal = true;
    
    // Reset states
    this.submitted = false;
    this.otpSent = false;
    this.isRegisterMode = false;
    this.useOTP = true;
    this.modalView = 'login';
    this.forgotSubmitted = false;
    this.resetSubmitted = false;
    this.resetOtpInputs();
    this.loginForm.reset({ rememberMe: false });
    this.registerForm.reset();
    this.forgotPasswordForm.reset();
    this.resetPasswordForm.reset();
    this.setOtpValidators();
    this.showConfirmDialog = false;
    this.isLoginRedirectFlow = false;
    this.otpSentMessage = '';
    this.pendingEmail = '';
    
    document.body.style.overflow = 'hidden';
  }

  closeLoginModal() {
    this.showLoginModal = false;
    this._pendingDay = null;
    this._pendingSlot = null;
    this.submitted = false;
    this.otpSent = false;
    this.isRegisterMode = false;
    this.modalView = 'login';
    this.forgotSubmitted = false;
    this.resetSubmitted = false;
    this.showConfirmDialog = false;
    this.isLoginRedirectFlow = false;
    this.otpSentMessage = '';
    this.pendingEmail = '';
    this.stopResendTimer();
    document.body.style.overflow = '';
  }

  closeModalOnBackdrop(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeLoginModal();
    }
  }

  // ── Slot click → check auth → open modal or go to checkout ───
  // checkout(day: any, slot: any) {
  //   const token = localStorage.getItem('TOKEN') || sessionStorage.getItem('TOKEN');

  //   if (token) {
  //     this.proceedToCheckout(day, slot);
  //   } else {
  //     this.openLoginModal(day, slot);
  //   }
  // }

  checkout(day: any, slot: any) {
    const token = localStorage.getItem('TOKEN') || sessionStorage.getItem('TOKEN');

    if (!token) {
      // No token at all — open popup immediately
      this.openLoginModal(day, slot);
      return;
    }

    // Token exists — verify it's still valid by hitting profile
    // this.isLoading = true;  // show a small spinner on the button

    this.auth.get_digicoffer_profile(true).subscribe({
      next: (user: any) => {
        // Token is valid — proceed
        // this.isLoading = false;
        this.proceedToCheckout(day, slot);
      },
      error: (err: any) => {
        // this.isLoading = false;

        if (err.status === 401) {
          // Session expired — clear token, open popup
          this.auth.clearStorage()
          this.openLoginModal(day, slot);
          // this.toastr.error('Session expired. Please login to continue.');
        } else {
          // Network/server error — don't clear token, just proceed
          // Checkout page will handle profile separately
          this.proceedToCheckout(day, slot);
        }
      }
    });
  }

  proceedToCheckout(day?: any, slot?: any) {
    const d = day ?? this._pendingDay;
    const s = slot ?? this._pendingSlot;
    if (!d || !s) return;

    const checkoutState = {
      date: d.date,
      slot: { slot_id: s.slot_id, slot_time: s.start_time },
      lawyer: {
        lawyer_id: this.lawyerId,
        amount: this.lawyer?.profile.consultation_fee.amount
      }
    };
    sessionStorage.setItem('checkout', JSON.stringify(checkoutState));
    localStorage.setItem('checkoutLog', (s.available === true).toString());
    this.router.navigate(['/consumer/checkout'], { state: checkoutState });
  }

  // ── Login Actions ──────────────────────────────────────────────

  // onLoginButtonClicked() {
  //   this.submitted = true;

  //   if (!this.isLoginFormValid()) return;

  //   const { emailOrMobile, password, otp, rememberMe } = this.loginForm.value;
  //   this.loginType = this.detectLoginType(emailOrMobile);
  //   this.currentEmailOrMobile = emailOrMobile;

  //   const loginPassword = this.useOTP ? '' : (password || '');
  //   const loginOTP = this.useOTP && this.otpSent ? (otp || '') : '';

  //   this.auth.login(emailOrMobile, loginPassword, this.loginType, 'login', loginOTP)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe({
  //       next: (res: HttpResponse<any>) => this.handleLoginResponse(res, rememberMe, password, emailOrMobile),
  //       error: () => this.toastr.error('Login failed. Please try again.', 'Error!')
  //     });
  // }
  onLoginButtonClicked() {

    this.submitted = true;

    this.loginForm.patchValue({
      otp: this.otpDigits.join('')
    });

    if (this.useOTP) {
      this.loginForm.patchValue({
        otp: this.otpDigits.join('')
      });
    }

    if (!this.isLoginFormValid()) {
      return;
    }

    const {
      emailOrMobile,
      password,
      otp,
      rememberMe
    } = this.loginForm.value;

    this.loginType = this.detectLoginType(emailOrMobile);

    this.currentEmailOrMobile = emailOrMobile;

    const loginPassword =
      this.useOTP ? '' : (password || '');

    const loginOTP =
      this.useOTP && this.otpSent
        ? otp
        : '';

    this.auth.login(
      emailOrMobile,
      loginPassword,
      this.loginType,
      'login',
      loginOTP
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: HttpResponse<any>) =>
          this.handleLoginResponse(
            res,
            rememberMe,
            password,
            emailOrMobile
          ),

        error: () =>
          this.toastr.error(
            'Login failed. Please try again.',
            'Error!'
          )
      });
  }

  private isLoginFormValid(): boolean {
    if (this.useOTP && !this.otpSent) return !this.loginForm.get('emailOrMobile')?.invalid;
    if (this.useOTP && this.otpSent) return !(this.loginForm.get('emailOrMobile')?.invalid || this.loginForm.get('otp')?.invalid);
    return !this.loginForm.invalid;
  }

  // private handleLoginResponse(res: HttpResponse<any>, rememberMe: boolean, password: string, emailOrMobile: string): void {

  //   const body = res.body;
  //   if (!body) { this.toastr.error('Invalid server response', 'Error!'); return; }

  //   if (body.otp_sent) {
  //     this.currentEmailOrMobile = this.loginForm.get('emailOrMobile')?.value;
  //     this.loginForm.patchValue({
  //         emailOrMobile: this.currentEmailOrMobile
  //     }); //preserve logs

  //     this.otpSent = true;
  //     this.submitted = false;
  //     const destination = this.loginType === 'email' ? 'email' : 'mobile number';
  //     this.toastr.success(body.msg || `OTP sent to your ${destination}`, 'Success!');
  //     this.startResendTimer();
  //     setTimeout(() => this.otpInputs?.first?.nativeElement.focus(), 100);
  //     return;
  //   }

  //   if (body.error) {
  //     this.toastr.error(body.msg || 'Login failed', 'Error!');
  //     return;
  //   }

  //   this.storeSessionData(body, emailOrMobile);
  //   this.handleRememberMe(rememberMe, password);
  //   this.toastr.success('Login successful');

  //   const day  = this._pendingDay;
  //   const slot = this._pendingSlot;

  //   this.closeLoginModal();
  //   setTimeout(() => this.proceedToCheckout(day, slot), 300);
  // }


  // ── Register Actions ───────────────────────────────────────────

  // onRegisterButtonClicked(): void {
  //   this.submitted = true;

  //   if (!this.otpSent) {
  //     // Validate Initial Fields
  //     if (this.registerForm.get('firstName')?.invalid || 
  //         this.registerForm.get('lastName')?.invalid || 
  //         this.registerForm.get('emailOrMobile')?.invalid) {
  //       return;
  //     }
  //     this.sendRegisterOTP();
  //   } else {
  //     // Validate OTP
  //     if (this.registerForm.get('otp')?.invalid) return;
  //     this.verifyRegisterOTP();
  //   }
  // }

  private handleLoginResponse(
    res: HttpResponse<any>,
    rememberMe: boolean,
    password: string,
    emailOrMobile: string
  ): void {

    const body = res.body;

    if (!body) {
      this.toastr.error('Invalid server response', 'Error!');
      return;
    }

    // OTP Sent
    if (body.otp_sent) {

      this.currentEmailOrMobile =
        this.loginForm.get('emailOrMobile')?.value;

      this.loginForm.patchValue({
        emailOrMobile: this.currentEmailOrMobile
      });

      this.otpSent = true;
      this.submitted = false;

      const destination =
        this.loginType === 'email'
          ? 'email'
          : 'mobile number';

      this.toastr.success(
        body.msg || `OTP sent to your ${destination}`,
        'Success!'
      );

      this.startResendTimer();

      setTimeout(() => {
        this.otpInputs?.first?.nativeElement.focus();
      }, 100);

      return;
    }

    // Login failed
    if (body.error) {
      this.toastr.error(body.msg || 'Login failed', 'Error!');
      return;
    }

    // Login Success
    this.storeSessionData(body, emailOrMobile);
    this.handleRememberMe(rememberMe, password);

    this.toastr.success('Login successful');

    this.isLoggedIn = true;

    const day = this._pendingDay;
    const slot = this._pendingSlot;

    this.closeLoginModal();

    setTimeout(() => {
      this.proceedToCheckout(day, slot);
    }, 300);
  }

  onRegisterButtonClicked(): void {
    this.trimRegisterFormFields();
    this.submitted = true;

    if (this.otpSent) {
      this.registerForm.patchValue({
        otp: this.otpDigits.join('')
      });
    }

    if (!this.otpSent) {

      if (
        this.registerForm.get('firstName')?.invalid ||
        this.registerForm.get('lastName')?.invalid ||
        this.registerForm.get('email')?.invalid ||
        this.registerForm.get('mobile')?.invalid
      ) {
        return;
      }

      // Show confirmation dialog instead of calling the API directly
      this.openRegisterConfirmDialog();

    } else {

      if (this.registerForm.get('otp')?.invalid) {
        return;
      }

      this.verifyRegisterOTP();
    }
  }

  /**
   * Open confirmation dialog with the current email and mobile (register flow)
   */
  openRegisterConfirmDialog(): void {
    const emailValue = this.registerForm.get('email')?.value?.trim().toLowerCase();
    const mobileValue = this.registerForm.get('mobile')?.value?.replace(/^0+/, '');
    this.confirmEmail = emailValue;
    this.confirmPhone = mobileValue;
    this.showConfirmDialog = true;
  }

  /**
   * User clicked "No, Edit" — close dialog, return to form
   */
  onRegisterConfirmCancel(): void {
    this.showConfirmDialog = false;
  }

  /**
   * User clicked "Yes, Continue" — close dialog and trigger the send-OTP API
   */
  onRegisterConfirmProceed(): void {
    this.showConfirmDialog = false;
    this.sendRegisterOTP();
  }

  private trimRegisterFormFields(): void {
    const emailControl = this.registerForm.get('email');
    if (emailControl) {
      emailControl.setValue(emailControl.value?.trim().toLowerCase() ?? '');
    }
    const mobileControl = this.registerForm.get('mobile');
    if (mobileControl) {
      mobileControl.setValue(mobileControl.value?.replace(/^0+/, '').trim() ?? '');
    }
    const firstNameControl = this.registerForm.get('firstName');
    if (firstNameControl) {
      firstNameControl.setValue(firstNameControl.value?.trim() ?? '');
    }
    const lastNameControl = this.registerForm.get('lastName');
    if (lastNameControl) {
      lastNameControl.setValue(lastNameControl.value?.trim() ?? '');
    }
  }

  private sendRegisterOTP(): void {
    const firstName = this.registerForm.get('firstName')?.value?.trim();
    const lastName = this.registerForm.get('lastName')?.value?.trim();
    const email = this.registerForm.get('email')?.value?.trim().toLowerCase();
    const mobile = this.registerForm.get('mobile')?.value?.replace(/^0+/, '');

    // Store for later use
    this.registeredFirstName = firstName;
    this.registeredLastName = lastName;
    this.currentEmailOrMobile = mobile;
    this.pendingEmail = email;
    this.registerType = 'mobile';
    this.isLoginRedirectFlow = false;

    // Call authService to send OTP via register API using mobile, also
    // forwarding email so the backend can detect an email-based conflict
    this.auth.sendOtpForRegistration(mobile, firstName, 'mobile', 'register', lastName, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.handleRegisterOtpSent(response);
        },
        error: (error) => {
          this.handleSendRegisterOtpError(error);
        }
      });
  }

  /**
   * Handle OTP sent successfully (register flow)
   */
  private handleRegisterOtpSent(response: HttpResponse<any>): void {
    const isSuccess = response.body?.otp_sent ||
                     (response.body?.error === false) ||
                     (response.status === 200 || response.status === 201);

    if (isSuccess) {
      const msg = response.body?.msg || 'OTP sent to your mobile number';

      // The backend may deliver the OTP via email even though registration
      // was submitted with logintype 'mobile' (e.g. it prioritizes a
      // verified email when both are provided). Detect the actual channel
      // from the response message so verify/resend target the right
      // identifier, and the banner/toast reflect what really happened.
      if (msg.toLowerCase().includes('email') && this.pendingEmail) {
        this.registerType = 'email';
        this.currentEmailOrMobile = this.pendingEmail;
      }

      this.otpSent = true;
      this.submitted = false;
      this.canResend = false;
      this.resendTimer = 60;
      this.otpSentMessage = msg;

      this.toastr.success(msg, 'Success!');
      this.startResendTimer();
      setTimeout(() => this.otpInputs?.first?.nativeElement.focus(), 100);
    } else {
      this.toastr.error('Failed to send OTP. Please try again.', 'Error!');
    }
  }

  /**
   * Handle send-OTP error (register flow), including the "already
   * registered" redirect_to case where we switch to logging the user in
   * via OTP instead.
   */
  private handleSendRegisterOtpError(error: any): void {
    const body = error?.error;
    const errorMessage = body?.message || body?.msg || 'Failed to send OTP. Please try again.';
    const redirectTo: 'email' | 'mobile' | 'both' | undefined = body?.redirect_to;

    // Already-in-use detection: prefer the structured redirect_to flag from
    // the API; fall back to the previous message-sniffing for backends/errors
    // that don't send redirect_to yet.
    const isAlreadyInUse =
      !!redirectTo ||
      errorMessage.toLowerCase().includes('already registered') ||
      errorMessage.toLowerCase().includes('already exists') ||
      errorMessage.toLowerCase().includes('already in use') ||
      errorMessage.toLowerCase().includes('already');

    if (!isAlreadyInUse) {
      this.toastr.error(errorMessage, 'Error!');
      return;
    }

    // Pick which channel to send the login OTP to, and the matching message.
    // redirect_to === 'email' or 'both' -> prioritize email.
    // redirect_to === 'mobile'          -> use mobile.
    // no redirect_to (legacy fallback)  -> keep prior behavior (mobile).
    let identifierType: 'email' | 'mobile';
    let identifierValue: string;
    let alreadyInUseMsg: string;

    if (redirectTo === 'email') {
      identifierType = 'email';
      identifierValue = this.pendingEmail;
      alreadyInUseMsg = 'Email is already registered. Please log in using the OTP sent to your inbox.';
    } else if (redirectTo === 'mobile') {
      identifierType = 'mobile';
      identifierValue = this.currentEmailOrMobile;
      alreadyInUseMsg = 'Phone number is already registered. Please log in using the OTP sent via SMS.';
    } else if (redirectTo === 'both') {
      identifierType = 'email';
      identifierValue = this.pendingEmail;
      alreadyInUseMsg = 'Account already exists. Please login via OTP sent to your inbox.';
    } else {
      identifierType = 'mobile';
      identifierValue = this.currentEmailOrMobile;
      alreadyInUseMsg = 'Account already exists. OTP sent successfully.';
    }

    this.registerType = identifierType;
    this.currentEmailOrMobile = identifierValue;
    this.isLoginRedirectFlow = true;

    // Call LOGIN API to send OTP to the resolved channel
    this.auth.login(identifierValue, '', identifierType, 'login')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body?.otp_sent || res.body?.error === false) {
            this.otpSent = true;
            this.submitted = false;
            this.canResend = false;
            this.resendTimer = 60;

            setTimeout(() => {
              this.otpInputs?.first?.nativeElement.focus();
            }, 100);

            this.registerForm.get('otp')?.setValidators([
              Validators.required,
              Validators.pattern(/^\d{6}$/)
            ]);
            this.registerForm.get('otp')?.updateValueAndValidity();

            this.startResendTimer();
            this.otpSentMessage = alreadyInUseMsg;
            this.toastr.success(alreadyInUseMsg);
          } else {
            this.toastr.error(
              res.body?.msg || 'Failed to send OTP',
              'Error!'
            );
          }
        },
        error: () => {
          this.toastr.error('Failed to send OTP. Please try again.', 'Error!');
        }
      });
  }
  

  private verifyRegisterOTP(): void {

    const otp = this.otpDigits.join('');

    if (!otp || otp.length !== 6) {
      this.registerForm.get('otp')?.setErrors({
        pattern: true
      });
      return;
    }

    // Existing account (redirect_to case) — verify via the same login API
    // used for OTP login, not the register-verify API.
    if (this.isLoginRedirectFlow) {

      this.auth.login(
        this.currentEmailOrMobile,
        '',
        this.registerType,
        'login',
        otp
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({

          next: (res: HttpResponse<any>) => {

            this.handleLoginResponse(
              res,
              false,
              '',
              this.currentEmailOrMobile
            );

          },

          error: () => {

            this.toastr.error(
              'OTP verification failed.',
              'Error!'
            );

          }

        });

      return;
    }

    // New User
    this.auth.verifyOtpForRegistration(
      this.currentEmailOrMobile,
      otp,
      this.registerType,
      'login'
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({

        next: (response: any) => {

          const body = response.body;

          if (!body) {
            this.toastr.error(
              'Invalid server response',
              'Error!'
            );
            return;
          }

          if (body.error) {
            this.toastr.error(
              body.msg || 'OTP verification failed.',
              'Error!'
            );
            return;
          }

          this.storeSessionData(
            body,
            this.currentEmailOrMobile
          );

          this.isLoggedIn = true;

          this.toastr.success(
            body.msg || 'Registration successful!',
            'Success!'
          );

          const day = this._pendingDay;
          const slot = this._pendingSlot;

          this.closeLoginModal();

          setTimeout(() => {
            this.proceedToCheckout(day, slot);
          }, 300);

        },

        error: (error) => {

          const errorMessage =
            error.error?.message ||
            error.error?.msg ||
            'OTP verification failed.';

          this.toastr.error(errorMessage, 'Error!');

        }

      });

  }

  // ── Shared Helpers ─────────────────────────────────────────────

  private storeSessionData(body: any, emailOrMobile: string): void {
    const data = body.data;
    const token = data.access_token;

    sessionStorage.setItem('TOKEN', token);
    localStorage.setItem('TOKEN', token);
    
    const resolvedType = this.isRegisterMode ? this.registerType : this.loginType;

    if (data) {
      localStorage.setItem('PK', data.pk);
      localStorage.setItem('REFRESH_TOKEN', data.refresh_token);
      localStorage.setItem('first_name', data.first_name || this.registeredFirstName);
      localStorage.setItem('last_name', data.last_name || this.registeredLastName);
      localStorage.setItem('email', data.email || (resolvedType === 'email' ? emailOrMobile : ''));
      localStorage.setItem('mobile', data.mobile || (resolvedType === 'mobile' ? emailOrMobile : ''));
      localStorage.setItem('full_name', `${data.first_name || this.registeredFirstName} ${data.last_name || this.registeredLastName}`.trim());
    }
    
    localStorage.setItem('login_type', resolvedType);
  }

  private handleRememberMe(rememberMe: boolean, password: string): void {
    if (!this.isRegisterMode) {
       if (rememberMe && !this.useOTP) {
        localStorage.setItem('remembered_email_or_mobile', this.currentEmailOrMobile);
        localStorage.setItem('remembered_password', password);
        localStorage.setItem('remembered_checked', 'true');
      } else {
        localStorage.removeItem('remembered_email_or_mobile');
        localStorage.removeItem('remembered_password');
        localStorage.removeItem('remembered_checked');
      }
    }
  }

  toggleLoginMethod() {
    this.useOTP = !this.useOTP;
    this.otpSent = false;
    this.submitted = false;
    this.resetOtpInputs();
    this.loginForm.patchValue({ otp: '' });

    if (this.useOTP) {
      this.setOtpValidators();
    } else {
      this.setPasswordValidators();
    }
  }

  setOtpValidators() {
    this.loginForm.get('password')?.clearValidators();
    this.loginForm.get('emailOrMobile')?.setValidators([Validators.required, this.validateEmailOrMobile.bind(this)]);
    this.loginForm.get('otp')?.setValidators([Validators.required, Validators.pattern(/^\d{6}$/)]);
    this.loginForm.get('password')?.updateValueAndValidity();
    this.loginForm.get('emailOrMobile')?.updateValueAndValidity();
    this.loginForm.get('otp')?.updateValueAndValidity();
  }

  setPasswordValidators() {
    this.loginForm.get('password')?.setValidators([Validators.required]);
    this.loginForm.get('emailOrMobile')?.setValidators([Validators.required, Validators.email]);
    this.loginForm.get('otp')?.clearValidators();
    this.loginForm.get('password')?.updateValueAndValidity();
    this.loginForm.get('emailOrMobile')?.updateValueAndValidity();
    this.loginForm.get('otp')?.updateValueAndValidity();
  }

  cancelOTP() {
    this.otpSent = false;
    this.submitted = false;
    this.resetOtpInputs();
    if (this.isRegisterMode) {
      this.registerForm.get('otp')?.reset();
      this.registerForm.patchValue({ otp: '' });
      this.isLoginRedirectFlow = false;
      this.otpSentMessage = '';
    } else {
      this.loginForm.get('otp')?.reset();
      this.loginForm.patchValue({ otp: '' });
    }
    this.stopResendTimer();
  }

  // ── OTP Inputs ─────────────────────────────────────────────────

  resetOtpInputs() { this.otpDigits = ['', '', '', '', '', '']; }

  onOtpDigitInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9]/g, '');
    if (value.length > 1) value = value.slice(-1);
    input.value = '';
    this.otpDigits[index] = value;
    input.value = value;
    
    // Update respective form
    const form = this.isRegisterMode ? this.registerForm : this.loginForm;
    form.patchValue({ otp: this.otpDigits.join('') }, { emitEvent: false });

    if (value.length === 1 && index < 5) {
      setTimeout(() => {
        const next = this.otpInputs.toArray()[index + 1]?.nativeElement;
        if (next) { next.focus(); next.select(); }
      }, 0);
    }
  }

  // Backspace deletes one digit *and* steps the cursor back, so clearing the
  // code box by box never leaves the cursor on the box just emptied.
  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const prev = input.previousElementSibling as HTMLInputElement | null;
    const next = input.nextElementSibling as HTMLInputElement | null;

    if (event.key === 'Backspace') {
      event.preventDefault();

      if (this.otpDigits[index]) {
        this.otpDigits[index] = '';
        input.value = '';
      } else if (prev) {
        this.otpDigits[index - 1] = '';
        prev.value = '';
      }

      const form = this.isRegisterMode ? this.registerForm : this.loginForm;
      form.patchValue({ otp: this.otpDigits.join('') }, { emitEvent: false });
      prev?.focus();
    } else if (event.key === 'ArrowLeft' && prev) {
      event.preventDefault();
      prev.focus();
    } else if (event.key === 'ArrowRight' && next) {
      event.preventDefault();
      next.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const digits = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    for (let i = 0; i < digits.length; i++) this.otpDigits[i] = digits[i];
    
    const form = this.isRegisterMode ? this.registerForm : this.loginForm;
    form.patchValue({ otp: this.otpDigits.join('') }, { emitEvent: false });

    setTimeout(() => {
      const inputs = this.otpInputs.toArray();
      inputs[Math.min(digits.length - 1, 5)]?.nativeElement.focus();
    }, 0);
  }

  startResendTimer() {
    this.resendTimer = 60;
    this.canResend = false;
    this.timerInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) { this.canResend = true; clearInterval(this.timerInterval); }
    }, 1000);
  }

  stopResendTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    this.resendTimer = 60;
    this.canResend = false;
  }

  resendOTP() {
    if (!this.canResend) return;
    this.submitted = false;
    this.resetOtpInputs();

    if (this.isRegisterMode) {
        // Resend for Register
        const emailOrMobile = this.currentEmailOrMobile;
        const type = this.registerType;
        // Using login endpoint for resend as per RegisterComponent logic
        this.auth.login(emailOrMobile, '', type, 'login')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: HttpResponse<any>) => {
              if (res.body?.otp_sent) {
                const destination = type === 'email' ? 'email' : 'mobile number';
                this.toastr.success(res.body.msg || `OTP resent to your ${destination}`, 'Success!');
                this.startResendTimer();
              } else {
                this.toastr.error('Failed to resend OTP', 'Error!');
              }
            },
            error: () => this.toastr.error('Failed to resend OTP', 'Error!')
          });
    } else {
        // Resend for Login
        const emailOrMobile = this.loginForm.value['emailOrMobile'];
        const loginType = this.detectLoginType(emailOrMobile);
        this.auth.login(emailOrMobile, '', loginType, 'login')
          .pipe(takeUntil(this.destroy$))
          .subscribe((res: HttpResponse<any>) => {
            if (res.body?.otp_sent) {
              this.toastr.success(res.body.msg || 'OTP resent', 'Success!');
              this.startResendTimer();
            } else {
              this.toastr.error('Failed to resend OTP', 'Error!');
            }
          }, () => this.toastr.error('Failed to resend OTP', 'Error!'));
    }
  }

  // ── Slot / Date Helpers ───────────────────────────────────────

  filterByExactDate(date: string) {
    this.selectedDateKey = date;
    this.filteredSlots = this.slots.filter(d => d.date === date);
    this.scrollActiveTabIntoView();
  }

  getAvailableCount(slots: any[]): number {
    return slots?.filter(s => s.available).length || 0;
  }

  buildDateTabs() {
    const today = new Date();
    const todayKey = this.getDateKey(today);
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowKey = this.getDateKey(tomorrow);

    this.dateTabs = this.slots.map((d: any) => {
      const availableCount =
        this.getAvailableCount(d.morning) + this.getAvailableCount(d.afternoon) +
        this.getAvailableCount(d.evening) + this.getAvailableCount(d.night);
      let label: string;
      if (d.date === todayKey) {
        label = 'Today';
      } else if (d.date === tomorrowKey) {
        label = 'Tomorrow';
      } else {
        const dateObj = new Date(d.date + 'T00:00:00');
        const dayName = dateObj.toLocaleString('en-US', { weekday: 'short' });
        const month = dateObj.toLocaleString('en-US', { month: 'short' });
        const day = dateObj.getDate();
        label = `${dayName} ${month} ${day}`;
      }
      return { label, date: d.date, available_slots: availableCount };
    });
  }

  getDateKey(date: Date) { return date.toISOString().split('T')[0]; }

  filterSlots(type: string) {
    const todayKey = this.getDateKey(new Date());
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowKey = this.getDateKey(tomorrow);
    if (type === 'today') this.filteredSlots = this.slots.filter(d => d.date === todayKey);
    else if (type === 'tomorrow') this.filteredSlots = this.slots.filter(d => d.date === tomorrowKey);
    else this.filteredSlots = this.slots.filter(d => d.date > tomorrowKey);
  }

  getSlotCount(type: string): number {
    const todayKey = this.getDateKey(new Date());
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowKey = this.getDateKey(tomorrow);
    let day: any;
    if (type === 'today') day = this.slots.find(d => d.date === todayKey);
    else if (type === 'tomorrow') day = this.slots.find(d => d.date === tomorrowKey);
    else day = this.slots.find(d => d.date > tomorrowKey);
    return day ? day.total_slots : 0;
  }

  selectDate(type: string) { this.selectedDate = type; this.filterSlots(type); }

  toggleMobileMenu() { this.mobileMenuOpen = !this.mobileMenuOpen; }

  getInitial(): string {
    return this.lawyer?.profile?.name ? this.lawyer.profile.name.charAt(0).toUpperCase() : 'L';
  }

  getVisibleSpecializations(areas: string[]): string {
    if (!areas || areas.length === 0) return '';
    return areas.join(', ');
  }

  formatLocation(address: any): string {
    if (!address) return '';
    const parts = [address.city, address.state].filter(Boolean);
    return parts.join(', ');
  }

  getExtraSpecializationsCount(areas: string[]): number {
    return 0;
  }

  // ── Single Lawyer Details: Overview / Available Slots tabs ──
  setProfileTab(tab: 'overview' | 'slots') {
    this.activeProfileTab = tab;
  }

  formatCourtName(name: string): string {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  formatCourtLocation(court: any): string {
    if (!court) return '';
    return [court.state, court.city].filter(Boolean).join(' \u00b7 ');
  }

  getCasesHandledText(): string {
    const cases = this.lawyer?.profile?.cases_handled;
    if (!cases || !cases.length) return '';
    return Array.isArray(cases) ? cases.join(' ') : String(cases);
  }

  /**
   * Cases Handled — normalises each entry (whether it's an object like
   * { title, description } or a plain "Title: description" string) into a
   * consistent { title, description } shape so each case can be rendered
   * as its own separated list item instead of one run-on paragraph.
   */
  get casesHandledItems(): { title: string; description: string }[] {
    const cases = this.lawyer?.profile?.cases_handled;
    if (!cases || !cases.length) return [];
    const list = Array.isArray(cases) ? cases : [cases];

    return list.map((c: any) => {
      if (c && typeof c === 'object') {
        return {
          title: c.title || c.case_type || c.name || c.heading || '',
          description: c.description || c.details || c.summary || ''
        };
      }
      const str = String(c || '').trim();
      // Split "Title: description" / "Title - description" style strings
      // into a bold heading + body; plain strings fall back to body only.
      const match = str.match(/^([^:\-–]{2,60})[:\-–]\s*(.+)$/);
      if (match) {
        return { title: match[1].trim(), description: match[2].trim() };
      }
      return { title: '', description: str };
    });
  }

  toggleCaseExpanded(index: number): void {
    this.casesExpandedMap[index] = !this.casesExpandedMap[index];
  }

  /**
   * Ensures every day's slots are correctly bucketed into morning / afternoon /
   * evening / night regardless of how the API returned them.
   *
   * Time boundaries:
   *   Morning   06:00 – 11:59
   *   Afternoon 12:00 – 16:59
   *   Evening   17:00 – 20:59
   *   Night     21:00 – 05:59
   */
  categoriseSlots(day: any): any {
    // Collect ALL slots from every bucket into one flat list
    const all: any[] = [
      ...(day.morning   || []),
      ...(day.afternoon || []),
      ...(day.evening   || []),
      ...(day.night     || []),
    ];

    const morning:   any[] = [];
    const afternoon: any[] = [];
    const evening:   any[] = [];
    const night:     any[] = [];

    for (const slot of all) {
      const timeStr: string = slot.start_time || '';
      // Parse "09:00 AM" / "05:30 PM" style strings
      const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) { morning.push(slot); continue; }

      let hour = parseInt(match[1], 10);
      const min = parseInt(match[2], 10);
      const meridiem = match[3].toUpperCase();

      if (meridiem === 'PM' && hour !== 12) hour += 12;
      if (meridiem === 'AM' && hour === 12) hour = 0;

      const totalMins = hour * 60 + min;

      if (totalMins >= 6 * 60 && totalMins < 12 * 60) {
        morning.push(slot);
      } else if (totalMins >= 12 * 60 && totalMins < 17 * 60) {
        afternoon.push(slot);
      } else if (totalMins >= 17 * 60 && totalMins < 21 * 60) {
        evening.push(slot);
      } else {
        night.push(slot);
      }
    }

    return { ...day, morning, afternoon, evening, night };
  }

  login() { this.router.navigate(['/consumer/login']); }

  scrollDateTabs(direction: -1 | 1) {
    const el = this.dateTabsScrollRef?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: direction * 160, behavior: 'smooth' });
  }

  onDateTabsScroll() {
    this.updateScrollArrows();
  }

  updateScrollArrows() {
    const el = this.dateTabsScrollRef?.nativeElement;
    if (!el) return;
    this.canScrollLeft = el.scrollLeft > 0;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  private scrollActiveTabIntoView() {
    setTimeout(() => {
      const el = this.dateTabsScrollRef?.nativeElement;
      if (!el) return;
      const active = el.querySelector('.date.active') as HTMLElement;
      if (active) {
        const elLeft = el.getBoundingClientRect().left;
        const elRight = elLeft + el.clientWidth;
        const tabLeft = active.getBoundingClientRect().left;
        const tabRight = active.getBoundingClientRect().right;
        if (tabLeft < elLeft || tabRight > elRight) {
          active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
      this.updateScrollArrows();
    }, 50);
  }

  restricttextSpace(event: any) {
    let v: string = event.target.value;
    v = v.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
    event.target.value = v;
  }

  retryLoadingProfile() {
    const storedId = localStorage.getItem('lawyerid');
    if (storedId) {
      this.isLoading = true;
      this.loadingError = '';
      this.ngOnInit();
    }
  }

  // ── Forgot / Reset Password (in-modal flow) ───────────────────

  goToForgotPassword() {
    this.modalView = 'forgot';
    this.forgotSubmitted = false;
    this.forgotPasswordForm.reset();
  }

  onForgotPasswordSubmit() {
    this.forgotSubmitted = true;
    if (this.forgotPasswordForm.invalid) return;
    const email = this.forgotPasswordForm.value.email;
    this.auth.forgot_password(email).subscribe({
      next: (res: HttpResponse<any>) => {
        if (!res.body?.error) {
          localStorage.setItem('reemail', email);
          localStorage.setItem('coffer_id', res.body.data.coffer_id);
          this.resetMail = email;
          this.resetPasswordForm.reset();
          this.resetSubmitted = false;
          this.hideResetPassword = false;
          this.hideConfirmPassword = false;
          this.modalView = 'reset';
        } else {
          this.toastr.error(res.body.msg || 'Something went wrong', 'Error!');
        }
      },
      error: (err: any) => {
        if (err.status === 404) {
          this.toastr.error(err.error?.msg, 'Error');
        } else {
          this.toastr.error('An unexpected error occurred. Please try again later.', 'Error!');
        }
      }
    });
  }

  onResetPasswordSubmit() {
    this.resetSubmitted = true;
    if (this.resetPasswordForm.invalid) return;
    const coffer_id = localStorage.getItem('coffer_id') ?? '';
    const { token, password } = this.resetPasswordForm.value;
    this.auth.submit_password(coffer_id, password, token).subscribe({
      next: (res: HttpResponse<any>) => {
        if (!res.body?.error) {
          this.toastr.success('Password reset successful. Please log in.');
          const rememberedEmail = localStorage.getItem('reemail') || '';
          this.loginForm.reset({ rememberMe: false });
          this.loginForm.patchValue({ emailOrMobile: rememberedEmail });
          this.useOTP = false;
          this.otpSent = false;
          this.isRegisterMode = false;
          this.resetSubmitted = false;
          this.modalView = 'login';
        } else {
          this.toastr.error(res.body.msg, 'Error!');
        }
      },
      error: (err: any) => {
        this.toastr.error(err.error?.msg || 'Something went wrong.', 'Error!');
      }
    });
  }

  resendResetToken() {
    const coffer_id = localStorage.getItem('coffer_id') ?? '';
    this.auth.resend_password_token(coffer_id).subscribe((res: HttpResponse<any>) => {
      if (res.body && res.body.error === false) {
        this.toastr.success(res.body.msg);
      }
    });
  }
}